import { getBrowserApiBase } from "./browser-api-base";

export interface RiskFlowStockChartPoint {
  time: number;
  close: number;
  high?: number;
  low?: number;
}

export interface RiskFlowStockChartData {
  label: string;
  tvSymbol: string;
  price: number | null;
  change: number | null;
  changePercent: number | null;
  previousClose: number | null;
  sparkline: RiskFlowStockChartPoint[];
  sparkline1d: RiskFlowStockChartPoint[];
  sparkline5d: RiskFlowStockChartPoint[];
  historySource: "public" | "yahoo" | "unavailable";
}

const API_BASE = getBrowserApiBase();
const chartCache = new Map<string, Promise<RiskFlowStockChartData | null>>();

export async function fetchRiskFlowStockChart(
  ticker: string,
): Promise<RiskFlowStockChartData | null> {
  const symbol = normalizeStockTicker(ticker);
  if (!symbol) return null;
  const cached = chartCache.get(symbol);
  if (cached) return cached;

  const request = fetch(
    `${API_BASE}/api/market-scan/equity-chart/${encodeURIComponent(symbol)}`,
    { signal: AbortSignal.timeout(6000) },
  )
    .then((response) => (response.ok ? response.json() : null))
    .then((json: { data?: unknown } | null) =>
      parseRiskFlowStockChartData(json?.data),
    )
    .catch(() => null);
  chartCache.set(symbol, request);
  return request;
}

export function prefetchRiskFlowStockCharts(tickers: string[]): void {
  for (const ticker of tickers) {
    void fetchRiskFlowStockChart(ticker);
  }
}

function parseRiskFlowStockChartData(
  value: unknown,
): RiskFlowStockChartData | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const label = typeof record.label === "string" ? record.label : "";
  if (!label) return null;
  return {
    label,
    tvSymbol: typeof record.tvSymbol === "string" ? record.tvSymbol : label,
    price: finiteNumberOrNull(record.price),
    change: finiteNumberOrNull(record.change),
    changePercent: finiteNumberOrNull(record.changePercent),
    previousClose: finiteNumberOrNull(record.previousClose),
    sparkline: parsePoints(record.sparkline),
    sparkline1d: parsePoints(record.sparkline1d),
    sparkline5d: parsePoints(record.sparkline5d),
    historySource: parseHistorySource(record.historySource),
  };
}

function parsePoints(value: unknown): RiskFlowStockChartPoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const time = finiteNumberOrNull(record.time);
      const close = finiteNumberOrNull(record.close);
      if (time == null || close == null) return null;
      return {
        time,
        close,
        ...(finiteNumberOrNull(record.high) != null
          ? { high: finiteNumberOrNull(record.high) as number }
          : {}),
        ...(finiteNumberOrNull(record.low) != null
          ? { low: finiteNumberOrNull(record.low) as number }
          : {}),
      };
    })
    .filter((point): point is RiskFlowStockChartPoint => Boolean(point));
}

function parseHistorySource(
  value: unknown,
): RiskFlowStockChartData["historySource"] {
  if (value === "public" || value === "yahoo") return value;
  return "unavailable";
}

function normalizeStockTicker(value: string): string | null {
  const ticker = value.replace(/^\$/, "").trim().toUpperCase();
  if (!/^[A-Z][A-Z0-9.]{0,5}$/.test(ticker)) return null;
  return ticker;
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
